import dayjs from 'dayjs'

import { CallType } from 'src/graphql/types/activityLogs'
import { BookingStatus } from 'src/graphql/types/bookings'
import { TaskStatus, TaskType } from 'src/graphql/types/tasks'

/**
 * Domain logic for the agenda widget: turns bookings and tasks into one
 * ordered list of rows.
 *
 * Kept free of React so the ordering, grouping and action rules can be unit
 * tested directly — they carry most of the product decisions:
 *
 * - Every row leads with the applicant's name; the name is the only route to
 *   the profile, so the trailing action is always *doing* something.
 * - Rows show a start time only. Duration lives in the subtitle.
 * - Tasks due before today are carryover: they have no honest slot on today's
 *   clock, so they group separately and show the date they were actually due.
 * - Every item carries its owner, so a list covering more than one person can
 *   name whose row each one is. Under a single-person scope the view drops it.
 */

/**
 * `EARLIER` only appears once the run of items behind `now` is long enough to
 * push the rest of the day off screen — see `EARLIER_FOLD_THRESHOLD`.
 */
export type AgendaGroupKey = 'OVERDUE' | 'EARLIER' | 'TODAY'

/**
 * Whose row this is. Bookings always have one (`Booking.employeeId` is
 * non-nullable); tasks may not, because `Task.assignees` can be empty — an
 * automated rule creating a task from a template with no static assignees and
 * no lead recruiter leaves it owned by nobody.
 */
export interface AgendaOwner {
  employeeId: string
  /** Display name, carrying a `+N` suffix when a task has several assignees. */
  name: string
}

/** What the trailing button does. Never navigation — that's the name. */
export type AgendaAction = 'CALL' | 'EMAIL' | 'NONE'

/** Drives the leading glyph. */
export type AgendaKind = 'BOOKING' | 'TASK'

export interface AgendaItem {
  /** `booking:<id>` / `task:<id>` — stable React key across a merged list. */
  id: string
  kind: AgendaKind
  /** Sort key. Booking start, or task due date. */
  at: Date
  /**
   * Applicant name when there is one. Tasks may have no applicant
   * (`Task.applicantId` is optional), in which case this is null and the task
   * title is used as the heading instead.
   */
  applicantName: string | null
  applicantId: string | null
  /** Heading text. Applicant name when present, else a fallback. */
  heading: string
  /** Second line — what this is. Never repeats the row's own time. */
  subtitle: string
  action: AgendaAction
  /** Booked by the AI voice agent. Drives the sparkle pill. */
  isAiBooked: boolean
  /** Voice agent name, for the pill tooltip. */
  voiceAgentName: string | null
  /** Dimmed: a finished appointment, a completed task, a cancelled booking. */
  isDimmed: boolean
  /** Struck through — a completed task. */
  isCompleted: boolean
  /**
   * Struck through — a booking the applicant or agent cancelled. Kept on the
   * agenda rather than filtered out: when the voice agent books something and
   * it later falls through, silently vanishing is worse than showing it gone.
   */
  isCancelled: boolean
  /** Task only, and only while incomplete. */
  isCompletable: boolean
  /**
   * Task priority, shown as the leading indicator. Null for bookings, which
   * lead with a calendar glyph instead.
   */
  priority: string | null
  /** Task only. Shown in the hover card beneath the title. */
  description: string | null
  /** Task only. Shown in the hover card. */
  reminderDate: Date | null
  taskId: string | null
  bookingId: string | null
  /** Overdue carryover: the date it was actually due, e.g. `Jul 14`. */
  overdueDueLabel: string | null
  /** Overdue carryover: how long it has waited, e.g. `15 days`. */
  overdueAgeLabel: string | null
  /** Whose row this is, for display. `null` means nobody owns it. */
  owner: AgendaOwner | null
  /**
   * Everyone who owns this row, for the "is this mine" test.
   *
   * Separate from `owner` because a task can have several assignees and the row
   * has one line: `owner` leads with the first and counts the rest, so matching
   * on it alone would strip a co-assignee of the actions on their own task.
   */
  ownerEmployeeIds: string[]
}

export interface AgendaGroup {
  key: AgendaGroupKey
  /** `Overdue` / `Today` — the count is rendered by the view. */
  label: string
  items: AgendaItem[]
}

/**
 * Above this many overdue items the group starts collapsed, so a long history
 * of late work can't push today's schedule below the fold.
 */
export const OVERDUE_COLLAPSE_THRESHOLD = 5

/**
 * Above this many items sitting behind `now`, they fold into their own
 * `Earlier today` group so the page opens on what is still ahead.
 *
 * Deliberately a size rule rather than a scope rule. One person's day rarely has
 * six items behind them by mid-afternoon, so their agenda keeps the inline NOW
 * marker; a whole team's day almost always does, and folds. A solo recruiter
 * having an unusually heavy day gets the fold too, which a scope rule would have
 * denied them.
 *
 * The trade is that the fold boundary *is* now, so the NOW marker is redundant
 * inside a folded day and the view stops drawing it.
 */
export const EARLIER_FOLD_THRESHOLD = 5

const buildName = (
  person?: {
    firstName?: string | null
    lastName?: string | null
  } | null
): string | null => {
  if (!person) return null
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ')
  return name.length > 0 ? name : null
}

/**
 * A task's action follows its type. `TaskType` defaults to `OTHER` and the
 * picker is skippable, so most real tasks land on `OTHER` — those get no
 * trailing button, since inventing a phone call for an untyped task would
 * claim a type the task doesn't have.
 *
 * A task with no applicant has nobody to contact, so it never gets an action
 * regardless of type.
 */
export const resolveTaskAction = (
  type: TaskType | string | null | undefined,
  hasApplicant: boolean
): AgendaAction => {
  if (!hasApplicant) return 'NONE'
  if (type === TaskType.CALL) return 'CALL'
  if (type === TaskType.EMAIL) return 'EMAIL'
  return 'NONE'
}

interface EmployeeLike {
  employeeId?: string | null
  firstName?: string | null
  lastName?: string | null
}

/**
 * A task's owner. `Task.assignees` is a many-to-many, so a task can have several
 * people on it — the first one leads and the rest become `+N`, since the row has
 * one line and no room to list them.
 *
 * An empty array is a real state, not a data error: an automated rule's
 * `CREATE_TASK` passes no creator, and the assignee fallback inside
 * `resolveTaskTemplate` only fires when one is present. Those tasks belong to
 * nobody and surface only on a company-wide agenda.
 */
export const resolveTaskOwner = (
  assignees?: EmployeeLike[] | null
): AgendaOwner | null => {
  const named = (assignees ?? []).filter((a) => a?.employeeId)
  const first = named[0]
  if (!first?.employeeId) return null
  const name = buildName(first) ?? 'Unnamed employee'
  return {
    employeeId: first.employeeId,
    name: named.length > 1 ? `${name} +${named.length - 1}` : name,
  }
}

/** Every employee id on a row, in the order the owner name was built from. */
export const resolveOwnerEmployeeIds = (
  people?: EmployeeLike[] | null
): string[] =>
  (people ?? [])
    .map((person) => person?.employeeId)
    .filter((id): id is string => Boolean(id))

/** A booking's owner — the recruiter the appointment belongs to. */
export const resolveBookingOwner = (
  employee?: EmployeeLike | null
): AgendaOwner | null => {
  if (!employee?.employeeId) return null
  return {
    employeeId: employee.employeeId,
    name: buildName(employee) ?? 'Unnamed employee',
  }
}

/** Detects a booking the AI voice agent made, via its activity log. */
export const isAiBookedBooking = (booking: {
  activityLog?: { callType?: string | null } | null
}): boolean => booking?.activityLog?.callType === CallType.BLANDAI

interface BookingLike {
  bookingId: string
  bookingStartTime?: string | Date | null
  bookingDurationInMinutes?: number | null
  title?: string | null
  status?: string | null
  applicant?: {
    applicantId?: string | null
    firstName?: string | null
    lastName?: string | null
  } | null
  activityLog?: { callType?: string | null } | null
  voiceAgentName?: string | null
  employee?: EmployeeLike | null
}

interface TaskLike {
  taskId: string
  title: string
  description?: string | null
  status?: string | null
  type?: string | null
  priority?: string | null
  dueDate?: string | Date | null
  reminderDate?: string | Date | null
  applicant?: {
    applicantId?: string | null
    firstName?: string | null
    lastName?: string | null
  } | null
  assignees?: EmployeeLike[] | null
}

/**
 * A booking's subtitle: what the meeting is, how long, and who booked it.
 * Deliberately excludes the start time — the time column already says that.
 */
/**
 * The line under an appointment's applicant name: its duration.
 *
 * The appointment title used to sit here ("Phone screen"), but with the voice
 * agent booking everything the titles are near-identical across rows, so they
 * carried no information. Duration is the thing that actually differs.
 *
 * Deliberately excludes the start time (the time column has it) and the AI
 * attribution (the sparkle pill states it). Falls back to the title when a
 * booking has no duration, so the line is never empty.
 */
const bookingSubtitle = (booking: BookingLike): string => {
  const parts: string[] = []
  if (booking.bookingDurationInMinutes) {
    parts.push(`${booking.bookingDurationInMinutes} min`)
  } else {
    const what = booking.title?.trim()
    parts.push(what && what.length > 0 ? what : 'Appointment')
  }
  // A struck-through row reads as "didn't happen" but not *why*.
  if (booking.status === BookingStatus.CANCELLED) {
    parts.push('cancelled')
  } else if (booking.status === BookingStatus.NO_SHOW) {
    parts.push('no show')
  }
  return parts.join(' · ')
}

export const toAgendaItemFromBooking = (
  booking: BookingLike,
  now: Date
): AgendaItem | null => {
  if (!booking.bookingStartTime) return null
  const start = dayjs(booking.bookingStartTime)
  if (!start.isValid()) return null

  const isAi = isAiBookedBooking(booking)
  const voiceAgentName = booking.voiceAgentName ?? null
  const applicantName = buildName(booking.applicant)
  const durationMins = booking.bookingDurationInMinutes ?? 0
  const hasEnded = start.add(durationMins, 'minute').isBefore(dayjs(now))
  // Both mean the appointment did not happen, so both get struck through.
  const isCancelled =
    booking.status === BookingStatus.CANCELLED ||
    booking.status === BookingStatus.NO_SHOW

  return {
    id: `booking:${booking.bookingId}`,
    kind: 'BOOKING',
    at: start.toDate(),
    applicantName,
    applicantId: booking.applicant?.applicantId ?? null,
    heading: applicantName ?? 'Appointment',
    subtitle: bookingSubtitle(booking),
    // Calling the person you're about to meet is useful whatever the meeting
    // is. Booking has no type enum, so we never infer the action from title.
    action: booking.applicant?.applicantId ? 'CALL' : 'NONE',
    isAiBooked: isAi,
    voiceAgentName,
    isDimmed: hasEnded || isCancelled,
    isCompleted: false,
    isCancelled,
    isCompletable: false,
    priority: null,
    description: null,
    reminderDate: null,
    taskId: null,
    bookingId: booking.bookingId,
    overdueDueLabel: null,
    overdueAgeLabel: null,
    owner: resolveBookingOwner(booking.employee),
    ownerEmployeeIds: resolveOwnerEmployeeIds(
      booking.employee ? [booking.employee] : []
    ),
  }
}

export const toAgendaItemFromTask = (
  task: TaskLike,
  now: Date
): AgendaItem | null => {
  if (!task.dueDate) return null
  const due = dayjs(task.dueDate)
  if (!due.isValid()) return null

  const applicantName = buildName(task.applicant)
  const hasApplicant = Boolean(task.applicant?.applicantId)
  const isCompleted = task.status === TaskStatus.COMPLETED
  const isOverdueCarryover = due.isBefore(dayjs(now).startOf('day'))

  return {
    id: `task:${task.taskId}`,
    kind: 'TASK',
    at: due.toDate(),
    applicantName,
    applicantId: task.applicant?.applicantId ?? null,
    // With no applicant there's no name to lead with, so the task title takes
    // the heading and the subtitle explains why it isn't a link.
    heading: applicantName ?? task.title,
    subtitle: applicantName ? task.title : 'No applicant attached',
    action: resolveTaskAction(task.type, hasApplicant),
    isAiBooked: false,
    voiceAgentName: null,
    isDimmed: isCompleted,
    isCompleted,
    isCancelled: false,
    isCompletable: !isCompleted,
    priority: task.priority ?? null,
    description: task.description ?? null,
    reminderDate: task.reminderDate ? dayjs(task.reminderDate).toDate() : null,
    taskId: task.taskId,
    bookingId: null,
    overdueDueLabel: isOverdueCarryover ? due.format('MMM D') : null,
    overdueAgeLabel: isOverdueCarryover
      ? formatOverdueAge(due.toDate(), now)
      : null,
    owner: resolveTaskOwner(task.assignees),
    ownerEmployeeIds: resolveOwnerEmployeeIds(task.assignees),
  }
}

/** `15 days` / `1 day` / `today`. Lateness the row position can't express. */
export const formatOverdueAge = (dueDate: Date, now: Date): string => {
  const days = dayjs(now)
    .startOf('day')
    .diff(dayjs(dueDate).startOf('day'), 'day')
  if (days <= 0) return 'today'
  return days === 1 ? '1 day' : `${days} days`
}

/**
 * Merges bookings and tasks into the two groups the widget renders.
 *
 * `OVERDUE` holds only tasks whose due date is before today — bookings are
 * never carryover, since a past booking simply happened.
 */
export const isSameDay = (a: Date, b: Date): boolean =>
  dayjs(a).isSame(dayjs(b), 'day')

/** `Today` when the day is today, else `Thu, Jul 31`. */
export const formatDayLabel = (selectedDate: Date, now: Date): string =>
  isSameDay(selectedDate, now)
    ? 'Today'
    : dayjs(selectedDate).format('ddd, MMM D')

export const buildAgendaGroups = ({
  bookings = [],
  tasks = [],
  now = new Date(),
  selectedDate,
}: {
  bookings?: BookingLike[]
  tasks?: TaskLike[]
  now?: Date
  /** The day being viewed. Defaults to today. */
  selectedDate?: Date
}): AgendaGroup[] => {
  const day = selectedDate ?? now
  const viewingToday = isSameDay(day, now)

  const items: AgendaItem[] = [
    ...bookings.map((b) => toAgendaItemFromBooking(b, now)),
    ...tasks.map((t) => toAgendaItemFromTask(t, now)),
  ].filter((item): item is AgendaItem => item !== null)

  const byTime = (a: AgendaItem, b: AgendaItem) =>
    a.at.getTime() - b.at.getTime()

  // Carryover only makes sense against today. On any other day every item
  // belongs to that day's list — a task due on the day you're looking at is not
  // "late", and yesterday's work is not carryover *of yesterday*.
  if (!viewingToday) {
    return [
      {
        key: 'TODAY',
        label: formatDayLabel(day, now),
        items: [...items].sort(byTime),
      },
    ]
  }

  const overdue = items
    .filter((item) => item.overdueDueLabel !== null)
    .sort(byTime)

  const today = items
    .filter((item) => item.overdueDueLabel === null)
    .sort(byTime)

  const groups: AgendaGroup[] = []
  if (overdue.length > 0) {
    groups.push({ key: 'OVERDUE', label: 'Overdue', items: overdue })
  }

  // Split at `now`, not at the fold threshold: the boundary is the same one the
  // NOW marker would have drawn, so folding never moves a row across it.
  const nowIndex = findNowMarkerIndex(today, now)
  const behind = today.slice(0, nowIndex)

  if (behind.length > EARLIER_FOLD_THRESHOLD) {
    groups.push({ key: 'EARLIER', label: 'Earlier today', items: behind })
    const ahead = today.slice(nowIndex)
    // A finished day has nothing left to come, and a `Still to come (0)` header
    // that expands to nothing is worse than no header: the view closes the day
    // out with the end-of-day line instead.
    if (ahead.length > 0) {
      // Renamed because `Today (13)` beside `Earlier today (10)` would read as
      // the whole day rather than the remainder of it.
      groups.push({ key: 'TODAY', label: 'Still to come', items: ahead })
    }
    return groups
  }

  groups.push({ key: 'TODAY', label: 'Today', items: today })
  return groups
}

/** Index of the first item at or after `now`, for placing the now marker. */
export const findNowMarkerIndex = (items: AgendaItem[], now: Date): number => {
  const ts = now.getTime()
  const idx = items.findIndex((item) => item.at.getTime() > ts)
  return idx === -1 ? items.length : idx
}
